import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, coverUrl } from "@/lib/api";
import VerifiedBadge from "@/components/VerifiedBadge";
import { Star, ArrowRight } from "lucide-react";

/**
 * Editorial spotlight rail. Horizontally scrolling large cards curated by admins.
 * Silently renders nothing when no featured drops exist.
 */
export default function FeaturedRail() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/featured").then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <section data-testid="featured-rail" className="border-b border-white/10 bg-[#0A0A0A]">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#FF3366] flex items-center gap-2">
              <Star className="w-3.5 h-3.5" /> // Editorial · Featured drops
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-2">
              Hand-picked this week.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.slice(0, 3).map((p) => (
            <Link
              key={p.id}
              to={`/p/${p.id}`}
              data-testid={`featured-card-${p.id}`}
              className="drop-card group relative border border-white/10 hover:border-[#FF3366]/50 overflow-hidden aspect-[3/2]"
            >
              <img
                src={coverUrl(p.cover_storage_path)}
                alt={p.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
              <div className="absolute top-4 left-4 flex gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] bg-[#FF3366] text-white px-2 py-1">
                  ★ FEATURED
                </span>
                {p.creator_verified && <VerifiedBadge testId={`featured-verified-${p.id}`} />}
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                  by {p.creator_name} · {p.category.toUpperCase()}
                </div>
                <div className="font-display font-black text-2xl tracking-tight leading-tight mt-1 line-clamp-2">
                  {p.headline || p.title}
                </div>
                {p.subtitle && (
                  <div className="mt-1 text-sm text-white/80 line-clamp-2">{p.subtitle}</div>
                )}
                <div className="mt-3 flex items-center justify-between">
                  <div className="font-mono text-lg font-bold text-[#CCFF00]">
                    ${p.price.toFixed(2)}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
                    {p.sold_count}/{p.total_stock} sold
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-black/70 border border-white/20 p-2">
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
